/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { aiAssistantAxios } from '../../utils/io/http/axios'
import './AIAnalytics.scss'
import { renderSchema } from './AIAnalytics'

// ─── Types ────────────────────────────────────────────────────────────────

interface AppDetails {
  id: string
  app_name: string
  organization_name: string | null
}

interface ConversationMessage {
  id: string
  conversationId: string
  role: 'user' | 'assistant'
  message: string
  statuses: string | null
  error: string | null
  action: string | null
  schema: SchemaDisplay | null
  createdAt: string
}

interface SchemaDisplay {
  [key: string]: unknown
}

interface ConversationFeedbackInfo {
  rating: 'thumbs_up' | 'thumbs_down'
  feedback: string | null
  createdAt: string
}

interface StatusDisplay {
  type: 'success' | 'error' | 'info'
  message: string
}

export const AIConversationPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>()
  const [appDetails, setAppDetails] = useState<AppDetails | null>(null)

  // Expanded conversation messages
  const [conversationMessages, setConversationMessages] = useState<
    ConversationMessage[]
  >([])
  const [conversationFeedback, setConversationFeedback] =
    useState<ConversationFeedbackInfo | null>(null)
  const [loadingConversation, setLoadingConversation] = useState<boolean>(true)

  const loadConversation = useCallback(async () => {
    setLoadingConversation(true)
    try {
      const { data } = await aiAssistantAxios.get<{
        appId: string
        messages: ConversationMessage[]
        feedback: ConversationFeedbackInfo | null
      }>(`/admin/ai/conversation/${conversationId}/messages?withAppId=true`)
      setConversationMessages(
        data.messages.map(m => ({
          ...m,

          ...(m.schema !== null && m.schema !== undefined
            ? { schema: JSON.parse(m.schema as unknown as string) }
            : {}),
        }))
      )
      setConversationFeedback(data.feedback)

      try {
        // Fetch app details
        const detailsResponse = await aiAssistantAxios.get<AppDetails>(
          `/admin/ai/app/${data.appId}/details`
        )
        setAppDetails(detailsResponse.data)
      } catch (err: unknown) {
        console.error('Failed to fetch app details', err)
        setAppDetails(null)
      }
    } catch (err) {
      console.error('Failed to fetch conversation messages', err)
    } finally {
      setLoadingConversation(false)
    }
  }, [conversationId])

  useEffect(() => {
    loadConversation()
  }, [conversationId])

  const parseStatuses = (statusesRaw: string | null): StatusDisplay[] => {
    if (!statusesRaw) return []
    try {
      const parsed = JSON.parse(statusesRaw)
      if (Array.isArray(parsed)) {
        return parsed.map((s: { type?: string; message?: string }) => ({
          type: s.type || 'info',
          message: s.message || String(s),
        }))
      }

      return []
    } catch {
      return []
    }
  }

  const getResponseTimeSeconds = (
    messages: ConversationMessage[],
    idx: number
  ): number | null => {
    const msg = messages[idx]
    if (!msg || msg.role !== 'assistant') return null

    const prevMsg = messages[idx - 1]
    if (!prevMsg || prevMsg.role !== 'user') return null

    const userTime = new Date(prevMsg.createdAt).getTime()
    const assistantTime = new Date(msg.createdAt).getTime()

    return (assistantTime - userTime) / 1000
  }

  const getResponseTimeClass = (seconds: number) => {
    if (seconds > 60) return 'slow'
    if (seconds > 30) return 'medium'

    return 'fast'
  }

  const formatResponseTime = (seconds: number): string => {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`

    return `${seconds.toFixed(0)}s`
  }

  return (
    <div className="ai-analytics-page">
      <div className="ai-analytics-header">
        {appDetails && (
          <div className="stats-flex" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="stat-label">Application Name</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>
                {appDetails.app_name || '—'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Organization</div>
              <div className="stat-value" style={{ fontSize: '20px' }}>
                {appDetails.organization_name || '—'}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="app-conversation-header" role="button" tabIndex={0}>
        <div
          className="conversation-thread"
          style={{ width: '100%', cursor: 'default' }}
        >
          {loadingConversation && (
            <div className="conversation-loading">Loading messages...</div>
          )}
          {conversationMessages && conversationMessages.length > 0 && (
            <div className="conversation-messages">
              {conversationMessages.map((msg, idx) => {
                const statuses = parseStatuses(msg.statuses)
                const responseSeconds = getResponseTimeSeconds(
                  conversationMessages,
                  idx
                )

                return (
                  <div key={msg.id} className={`conversation-msg ${msg.role}`}>
                    <div className="conversation-msg-header">
                      <span className={`conversation-msg-role ${msg.role}`}>
                        {msg.role === 'user' ? 'User' : 'AI Assistant'}
                      </span>
                      <span className="conversation-msg-time">
                        {new Date(msg.createdAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {responseSeconds !== null && (
                        <span
                          className={`conversation-msg-response-time ${getResponseTimeClass(
                            responseSeconds
                          )}`}
                        >
                          Response: {formatResponseTime(responseSeconds)}
                        </span>
                      )}
                      {msg.error && (
                        <span className="conversation-msg-error-badge">
                          Error
                        </span>
                      )}
                    </div>
                    <div className="conversation-msg-body">{msg.message}</div>
                    {msg.schema !== null &&
                      msg.schema !== undefined &&
                      renderSchema(msg as unknown as Parameters<typeof renderSchema>[0])}
                    {statuses.length > 0 && (
                      <div className="conversation-msg-statuses">
                        {statuses.map(s => (
                          <span
                            key={`${msg.id}-${s.type}-${s.message.slice(
                              0,
                              20
                            )}`}
                            className={`conversation-status-badge status-${s.type}`}
                          >
                            {s.type === 'success' && '✓ '}
                            {s.type === 'error' && '✕ '}
                            {s.type === 'info' && 'ℹ '}
                            {s.message}
                          </span>
                        ))}
                      </div>
                    )}
                    {msg.error && (
                      <div className="conversation-msg-error">{msg.error}</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {conversationFeedback && (
            <div
              className={`conversation-feedback-banner ${conversationFeedback.rating}`}
            >
              <span className="conversation-feedback-icon">
                {conversationFeedback.rating === 'thumbs_up' ? '👍' : '👎'}
              </span>
              <div className="conversation-feedback-detail">
                <span className="conversation-feedback-label">
                  User rated this{' '}
                  {conversationFeedback.rating === 'thumbs_up'
                    ? 'helpful'
                    : 'not helpful'}
                </span>
                {conversationFeedback.feedback && (
                  <span className="conversation-feedback-text">
                    &quot;
                    {conversationFeedback.feedback}
                    &quot;
                  </span>
                )}
              </div>
            </div>
          )}
          {conversationMessages && conversationMessages.length === 0 && (
            <div className="conversation-loading">No messages found.</div>
          )}
        </div>
      </div>
    </div>
  )
}
